import * as React from 'react';
import { styled } from '../theme-provider';
import { getCommonProps } from '../helpers/common-props';
import { SSRContext } from '../helpers/ssr';
import { useIsClientOnlyRendering } from '../helpers/hooks/use-is-client-only-rendering';
// make sure to keep all values in both the type and the predicate function!
function isAspectRatioValue(value) {
    return [
        '1:2',
        '9:16',
        '2:3',
        '3:4',
        '4:5',
        '1:1',
        '6:5',
        '4:3',
        '3:2',
        '10:6',
        '16:9',
        '2:1',
    ].includes(value);
}
const combinedMediaQuery = (props) => {
    const minQuery = `(min-aspect-ratio: ${props.minAspectRatio ? props.minAspectRatio.replace(':', '/') : '0/1'})`;
    const maxQuery = props.maxAspectRatio &&
        `(max-aspect-ratio: ${props.maxAspectRatio.replace(':', '/')})`;
    return [minQuery, maxQuery].filter(Boolean).join(' and ');
};
const StyledAspectRatioWrapper = styled.div.withConfig({ displayName: "StyledAspectRatioWrapper", componentId: "sc-td1kzr" }) `display:none;@media ${combinedMediaQuery}{display:contents;}${({ maxAspectRatio }) => maxAspectRatio &&
    `@media (aspect-ratio: ${maxAspectRatio.replace(':', '/')}) {
			display: none;
		}`}`;
export function AspectRatioWrapper(props) {
    const { min, max, children } = props;
    const commonProps = getCommonProps(props);
    const { ssrAspectRatio } = React.useContext(SSRContext);
    const isRenderedInClient = useIsRenderedInClient();
    const isRenderedInClientOnly = useIsClientOnlyRendering();
    const { notHydrating } = React.useContext(SSRContext);
    const isClientOnly = notHydrating !== null && notHydrating !== void 0 ? notHydrating : isRenderedInClientOnly;
    const aspectRatioMatches = useAspectRatioMatchesListener(min, max);
    const aspectRatioMatchesForSsr = doesAspectRatioMatch(min, max, ssrAspectRatio);
    // SSR - always render, unless there is a ssrAspectRatio and it does not match min and max values
    if (!isRenderedInClient && !aspectRatioMatchesForSsr) {
        return null;
    }
    // client with hydration - uses the same condition as for SSR
    if (isRenderedInClient && !isClientOnly && !aspectRatioMatchesForSsr) {
        return null;
    }
    // client only, without hydration - render unless aspect ratio does not match min and max values
    if (isRenderedInClient && isClientOnly && !aspectRatioMatches) {
        return null;
    }
    return (React.createElement(StyledAspectRatioWrapper, Object.assign({ minAspectRatio: min, maxAspectRatio: max }, commonProps), children));
}
function useMediaQueryListener(query) {
    const mql = React.useMemo(() => typeof window !== 'undefined' && query && window.matchMedia(query), [query]);
    const [matches, setMatches] = React.useState(mql ? mql.matches : false);
    React.useEffect(() => {
        if (!mql) {
            return;
        }
        setMatches(mql.matches);
        const handleMediaQueryListener = (event) => {
            setMatches(event.matches);
        };
        mql.addEventListener('change', handleMediaQueryListener);
        return () => {
            mql.removeEventListener('change', handleMediaQueryListener);
        };
    }, [mql]);
    return matches;
}
function doesAspectRatioMatch(minAspectRatio, maxAspectRatio, ssrAspectRatio) {
    // if there is no given aspectRatio, we should always render the wrapped content
    // (it should be hidden by the css anyway)
    if (!ssrAspectRatio || !isAspectRatioValue(ssrAspectRatio)) {
        return true;
    }
    const ssrAspectRatioValue = calculateAspectRatio(ssrAspectRatio);
    if (minAspectRatio && maxAspectRatio) {
        const minAspectRatioValue = calculateAspectRatio(minAspectRatio);
        const maxAspectRatioValue = calculateAspectRatio(maxAspectRatio);
        return (ssrAspectRatioValue >= minAspectRatioValue &&
            ssrAspectRatioValue < maxAspectRatioValue);
    }
    if (minAspectRatio) {
        const minAspectRatioValue = calculateAspectRatio(minAspectRatio);
        return ssrAspectRatioValue >= minAspectRatioValue;
    }
    if (maxAspectRatio) {
        const maxAspectRatioValue = calculateAspectRatio(maxAspectRatio);
        return ssrAspectRatioValue < maxAspectRatioValue;
    }
    // without min or max values it's considered a match
    return true;
}
function calculateAspectRatio(aspectRatio) {
    const [width, height] = aspectRatio.split(':');
    return parseInt(width, 10) / parseInt(height, 10);
}
function useAspectRatioMatchesListener(minAspectRatio, maxAspectRatio) {
    const query = React.useMemo(() => combinedMediaQuery({ minAspectRatio, maxAspectRatio }), [minAspectRatio, maxAspectRatio]);
    return useMediaQueryListener(query);
}
function useIsRenderedInClient() {
    const [isRenderedInClient, setIsRenderedInClient] = React.useState(false);
    // useEffect only happens when we are in the browser not on the server
    // (except when testing with react-test-renderer)
    React.useEffect(() => {
        if (!isRenderedInClient) {
            // we need to check if we are really in the browser here
            // because of react-test-renderer
            setIsRenderedInClient(typeof window !== 'undefined');
        }
    }, [isRenderedInClient]);
    return isRenderedInClient;
}
